<template>
  <Teleport to="body">
    <div
      ref="wrapper"
      v-if="active"
      class="layout-modal-wrapper"
      :class="{ open: is_open, closing: closing }"
    >
      <div ref="content" class="layout-modal">
        <slot />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useDynformStore } from '@/stores/gae/store_dynform'
import { useUserStore } from '@/stores/gae/store_user'
import { onClickOutside } from '@vueuse/core'
import { nextTick, onBeforeUnmount, ref } from 'vue'

const is_open = ref(false)
const active = ref(false)
const closing = ref(false)
// Whether this modal instance currently holds a +1 on the busy counter,
// so unbalanced open/close calls can never desynchronize it.
const counted = ref(false)
const content = ref<HTMLElement>()
const wrapper = ref()

const emits = defineEmits(['onClosed'])

const props = withDefaults(
  defineProps<{
    canClose?: boolean
  }>(),
  {
    canClose: true
  }
)
const user_store = useUserStore()
const dynform_store = useDynformStore()

async function open() {
  return new Promise<void>((resolve) => {
    document.body.style.overflowY = 'hidden'
    if (!counted.value) {
      user_store.setBusy(true)
      counted.value = true
    }
    active.value = true
    nextTick().then(() => {
      is_open.value = true
      resolve()
    })
  })
}

function close(force: boolean = false, remove_overflow_hidden: boolean = true) {
  if (!force && !props.canClose) return
  if (remove_overflow_hidden) document.body.style.overflowY = ''
  closing.value = true
  is_open.value = false
  if (counted.value) {
    user_store.setBusy(false)
    counted.value = false
  }
  emits('onClosed')

  setTimeout(() => {
    active.value = false
    closing.value = false
  }, 550)
}

onClickOutside(content, () => {
  if (!dynform_store.in_group) {
    close()
  }
})

// A modal destroyed while still open (e.g. navigation triggered from inside
// the modal) must release its slot on the busy counter and the body scroll,
// otherwise the periodic refresh stays blocked for the whole session.
onBeforeUnmount(() => {
  if (counted.value) {
    document.body.style.overflowY = ''
    user_store.setBusy(false)
    counted.value = false
  }
})

defineExpose({ open, close })
</script>

<style scoped>
@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateX(50px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInWrapper {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes closing {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.layout-modal-wrapper {
  animation: 600ms cubic-bezier(0.19, 1, 0.22, 1) 0s 1 fadeInWrapper;
  opacity: 0;
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100;
  background-color: rgba(var(--vcrgb-dark), 0.6);
  overflow-x: hidden;
  pointer-events: all;
  overflow-y: hidden;
}

.layout-modal-wrapper.open {
  opacity: 1;
}

.layout-modal-wrapper.closing {
  pointer-events: none;
  opacity: 1;
  animation: 600ms cubic-bezier(0.19, 1, 0.22, 1) 0s 1 closing;
}

.layout-modal {
  animation: 600ms cubic-bezier(0.19, 1, 0.22, 1) 0s 1 fadeIn;
  transition: 600ms cubic-bezier(0.19, 1, 0.22, 1);
  position: absolute;
  box-sizing: border-box;
  top: 0;
  width: 800px;
  height: 100%;
  background-color: var(--vc-white);
  right: 0;
  overflow-y: scroll;
}

@media screen and (max-width: 1024px) {
  .layout-modal {
    width: 100%;
  }
}
</style>
